import { NavPublic } from "@/components/NavPublic";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export function DemoPage() {
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    organization_name: "",
    number_of_homes: "",
    message: "",
    data_consent: false,
    marketing_consent: false,
  });

  const submitMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest("POST", "/api/demo-enquiries", {
        ...data,
        number_of_homes: data.number_of_homes ? parseInt(data.number_of_homes) : null,
      });
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit demo request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.data_consent) {
      toast({
        title: "Consent Required",
        description: "Please consent to data storage to submit your request.",
        variant: "destructive",
      });
      return;
    }
    submitMutation.mutate(formData);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavPublic />
      
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="page-title text-3xl font-bold text-gray-900 mb-4">Book a Demo</h1>
            <p className="text-gray-600">
              Get a personalized walkthrough of Smart Care Audits and see how we can help streamline your compliance management.
            </p>
          </div>

          {submitted ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="text-green-600 text-5xl mb-4">✓</div>
                <h2 className="text-2xl font-semibold mb-2">Thank you!</h2>
                <p className="text-gray-600">
                  We've received your demo request. Our team will contact you within 1 business day to schedule your personalized demonstration.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Request a Demo</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                      data-testid="input-email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.organization_name}
                      onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                      data-testid="input-organization"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Care Homes
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.number_of_homes}
                      onChange={(e) => setFormData({ ...formData, number_of_homes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                      data-testid="input-homes"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information
                    </label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                      placeholder="Tell us about your specific needs or questions..."
                      data-testid="input-message"
                    />
                  </div>

                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="data-consent"
                        checked={formData.data_consent}
                        onCheckedChange={(checked) => setFormData({ ...formData, data_consent: checked as boolean })}
                        data-testid="checkbox-data-consent"
                      />
                      <label htmlFor="data-consent" className="text-sm text-gray-700 leading-tight cursor-pointer">
                        I consent to Smart Care Audits storing my information for the purpose of responding to my demo request. *
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="marketing-consent"
                        checked={formData.marketing_consent}
                        onCheckedChange={(checked) => setFormData({ ...formData, marketing_consent: checked as boolean })}
                        data-testid="checkbox-marketing-consent"
                      />
                      <label htmlFor="marketing-consent" className="text-sm text-gray-700 leading-tight cursor-pointer">
                        I would like to receive updates and marketing information about Smart Care Audits (optional)
                      </label>
                    </div>

                    <p className="text-xs text-gray-500 pt-2">
                      By submitting this form, you agree to our <a href="/privacy" className="text-purple-600 hover:underline">Privacy Policy</a> and <a href="/terms" className="text-purple-600 hover:underline">Terms of Service</a>.
                    </p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={submitMutation.isPending}
                    data-testid="button-submit-demo"
                  >
                    {submitMutation.isPending ? "Submitting..." : "Request Demo"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
